import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Settings, ArrowLeft, CheckCircle2, Globe, Upload, FileText, Sliders, Zap, AlertTriangle, Trash2, Sparkles, Users, Mail, Copy, Plus, X, Loader2 } from 'lucide-react';
import { getProjectBySlug, updateProject, uploadProjectLogo, getProjectMembers, inviteProjectMember, removeProjectMember, revokeProjectInvite } from '../../lib/apiClient';

export default function BrandSettingsPage() {
  const { brandSlug } = useParams();
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('profile'); // 'profile' | 'voice' | 'team'

  useEffect(() => {
    async function loadProject() {
      try {
        setLoading(true);
        const res = await getProjectBySlug(brandSlug);
        setProject(res?.data || null);
      } catch (e) {
        console.error('Failed to load brand for settings', e);
      } finally {
        setLoading(false);
      }
    }
    if (brandSlug) loadProject();
  }, [brandSlug]);

  if (loading) {
    return (
      <div className="min-h-screen bg-background text-white flex items-center justify-center">
        <span className="text-sm text-slate-400">Loading brand settings…</span>
      </div>
    );
  }

  if (!project) {
    return (
      <div className="min-h-screen bg-background text-white flex items-center justify-center">
        <div className="text-center space-y-2">
          <div className="text-lg font-semibold">Brand not found</div>
          <Link to="/app/projects" className="text-sm text-primary hover:text-white">
            Back to Projects
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background text-white font-sans pb-20">
      <header className="bg-surface/50 border-b border-white/10 sticky top-0 z-10 backdrop-blur-md">
        <div className="container mx-auto max-w-6xl px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link
              to={`/app/brand/${brandSlug}/dashboard`}
              className="p-2 rounded-lg hover:bg-white/5 text-slate-400 hover:text-white transition-colors flex items-center gap-1 text-xs"
            >
              <ArrowLeft className="w-4 h-4" />
              Dashboard
            </Link>
            <div className="w-9 h-9 rounded-xl bg-blue-500 flex items-center justify-center font-bold text-white text-sm">
              {project.name.substring(0, 2).toUpperCase()}
            </div>
            <div>
              <div className="text-sm font-semibold text-white leading-tight">{project.name}</div>
              <div className="text-[11px] text-slate-500 flex items-center gap-2">
                <span>Brand Settings</span>
                <span className="w-1 h-1 rounded-full bg-slate-600" />
                <span className="uppercase text-[10px] tracking-wide text-slate-500">{project.userRole || 'Member'}</span>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-3 text-xs text-slate-400">
            <Settings className="w-4 h-4" />
            <span>Settings</span>
          </div>
        </div>
      </header>

      <main className="container mx-auto max-w-6xl px-6 py-8 space-y-8">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-white mb-1">Brand Settings</h1>
            <p className="text-xs text-slate-400">Manage profile, voice, and configuration for this brand.</p>
          </div>
        </div>

        <div className="flex items-center gap-2 border-b border-white/10 text-xs">
          <button
            className={`px-3 py-2 rounded-t-lg border-b-2 transition-colors ${
              activeTab === 'profile'
                ? 'border-primary text-white'
                : 'border-transparent text-slate-400 hover:text-white hover:border-white/20'
            }`}
            onClick={() => setActiveTab('profile')}
          >
            Brand Profile
          </button>
          <button
            className={`px-3 py-2 rounded-t-lg border-b-2 transition-colors ${
              activeTab === 'voice'
                ? 'border-primary text-white'
                : 'border-transparent text-slate-400 hover:text-white hover:border-white/20'
            }`}
            onClick={() => setActiveTab('voice')}
          >
            Brand Voice
          </button>
          <button
            className={`px-3 py-2 rounded-t-lg border-b-2 transition-colors ${
              activeTab === 'team'
                ? 'border-primary text-white'
                : 'border-transparent text-slate-400 hover:text-white hover:border-white/20'
            }`}
            onClick={() => setActiveTab('team')}
          >
            Team
          </button>
        </div>

        {activeTab === 'profile' && (
          <BrandProfileSection
            project={project}
            onProjectUpdated={(updated) => {
              if (updated) {
                setProject((prev) => ({ ...prev, ...updated }));
              }
            }}
          />
        )}
        {activeTab === 'voice' && <BrandVoiceSection />}
        {activeTab === 'team' && <BrandTeamSection project={project} />}
      </main>
    </div>
  );
}

function BrandProfileSection({ project, onProjectUpdated }) {
  const [name, setName] = useState(project.name || '');
  const [description, setDescription] = useState(project.description || '');
  const [clientName, setClientName] = useState(project.client_name || '');
  const [clientEmail, setClientEmail] = useState(project.client_contact_email || '');
  const [logoUrl, setLogoUrl] = useState(project.logo_url || '');
  const [websiteUrl, setWebsiteUrl] = useState(project.website_url || '');
  const [saving, setSaving] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [status, setStatus] = useState('');
  const [error, setError] = useState('');

  const hasChanges =
    name !== (project.name || '') ||
    description !== (project.description || '') ||
    clientName !== (project.client_name || '') ||
    clientEmail !== (project.client_contact_email || '') ||
    logoUrl !== (project.logo_url || '') ||
    websiteUrl !== (project.website_url || '');

  async function handleSave() {
    if (!project?.id || !hasChanges || saving) return;
    try {
      setSaving(true);
      setStatus('');
      setError('');
      const res = await updateProject(project.id, {
        name,
        description,
        clientName,
        clientContactEmail: clientEmail,
        logoUrl,
        websiteUrl,
      });
      const updated = res?.data || null;
      if (updated && onProjectUpdated) {
        onProjectUpdated(updated);
      }
      setStatus('Saved');
    } catch (e) {
      console.error('Failed to update project profile', e);
      setError(e.message || 'Failed to save changes');
    } finally {
      setSaving(false);
    }
  }

  async function handleLogoFileSelected(file) {
    if (!project?.id || !file || uploadingLogo) return;
    try {
      setUploadingLogo(true);
      setStatus('');
      setError('');
      const res = await uploadProjectLogo(project.id, file);
      const updatedProject = res?.data?.project || null;
      const newLogoUrl = updatedProject?.logo_url || null;
      if (newLogoUrl) {
        setLogoUrl(newLogoUrl);
      }
      if (updatedProject && onProjectUpdated) {
        onProjectUpdated(updatedProject);
      }
      setStatus('Logo uploaded');
    } catch (e) {
      console.error('Failed to upload logo', e);
      setError(e.message || 'Failed to upload logo');
    } finally {
      setUploadingLogo(false);
    }
  }

  return (
    <section className="bg-surface border border-white/5 rounded-2xl p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-white">Brand Profile</h2>
          <p className="text-xs text-slate-400">Edit how this brand appears across PodVault.</p>
        </div>
      </div>

      <div className="grid md:grid-cols-3 gap-8">
        <div className="space-y-4 md:col-span-2">
          <div>
            <label className="block text-[11px] font-semibold text-slate-400 uppercase mb-1">Brand Name</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full bg-black/20 border border-white/10 rounded-lg px-3 py-2 text-sm text-slate-100 focus:outline-none focus:border-primary"
            />
          </div>
          <div>
            <label className="block text-[11px] font-semibold text-slate-400 uppercase mb-1">Description</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="w-full bg-black/20 border border-white/10 rounded-lg px-3 py-2 text-sm text-slate-100 focus:outline-none focus:border-primary h-24 resize-none"
            />
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="block text-[11px] font-semibold text-slate-400 uppercase mb-1">Client Name</label>
              <input
                type="text"
                value={clientName}
                onChange={(e) => setClientName(e.target.value)}
                className="w-full bg-black/20 border border-white/10 rounded-lg px-3 py-2 text-sm text-slate-100 focus:outline-none focus:border-primary"
              />
            </div>
            <div>
              <label className="block text-[11px] font-semibold text-slate-400 uppercase mb-1">Client Email</label>
              <input
                type="email"
                value={clientEmail}
                onChange={(e) => setClientEmail(e.target.value)}
                className="w-full bg-black/20 border border-white/10 rounded-lg px-3 py-2 text-sm text-slate-100 focus:outline-none focus:border-primary"
              />
            </div>
            <div>
              <label className="block text-[11px] font-semibold text-slate-400 uppercase mb-1">Website URL</label>
              <input
                type="url"
                value={websiteUrl}
                onChange={(e) => setWebsiteUrl(e.target.value)}
                className="w-full bg-black/20 border border-white/10 rounded-lg px-3 py-2 text-sm text-slate-100 focus:outline-none focus:border-primary"
                placeholder="https://example.com"
              />
            </div>
          </div>
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-[11px] font-semibold text-slate-400 uppercase mb-1">Logo</label>
            <div className="flex items-center gap-3">
              <div className="w-14 h-14 rounded-xl bg-blue-500 flex items-center justify-center overflow-hidden text-white font-bold text-lg">
                {logoUrl ? (
                  <img src={logoUrl} alt={project.name} className="w-full h-full object-cover" />
                ) : (
                  project.name.substring(0, 2).toUpperCase()
                )}
              </div>
              <label className={`px-3 py-1.5 rounded-lg border border-white/10 text-xs text-slate-200 hover:bg-white/5 ${uploadingLogo ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'}`}>
                {uploadingLogo ? 'Uploading…' : 'Upload File'}
                <input
                  type="file"
                  accept="image/png,image/jpeg,image/webp"
                  disabled={uploadingLogo}
                  className="hidden"
                  onChange={(e) => {
                    const file = e.target.files && e.target.files[0];
                    e.target.value = '';
                    if (file) handleLogoFileSelected(file);
                  }}
                />
              </label>
            </div>
            <div className="text-[11px] text-slate-500 mt-2 leading-relaxed">
              Recommended: square image, 512×512. Allowed: PNG/JPG/WebP. Max 2MB.
            </div>
          </div>
          <div>
            <label className="block text-[11px] font-semibold text-slate-400 uppercase mb-1">Logo URL</label>
            <input
              type="url"
              value={logoUrl}
              onChange={(e) => setLogoUrl(e.target.value)}
              className="w-full bg-black/20 border border-white/10 rounded-lg px-3 py-2 text-sm text-slate-100 focus:outline-none focus:border-primary"
              placeholder="https://example.com/logo.png"
            />
          </div>
          {error && (
            <div className="text-[11px] text-red-400">
              {error}
            </div>
          )}
          {!error && status && (
            <div className="text-[11px] text-green-400">
              {status}
            </div>
          )}
        </div>
      </div>

      <div className="flex justify-end border-t border-white/10 pt-4 mt-2">
        <button
          onClick={handleSave}
          disabled={saving || !hasChanges}
          className={`px-4 py-2 rounded-lg text-xs font-medium transition-colors ${
            saving || !hasChanges
              ? 'bg-white/5 text-slate-500 cursor-not-allowed'
              : 'bg-primary text-white hover:bg-primary-hover cursor-pointer'
          }`}
        >
          {saving ? 'Saving…' : hasChanges ? 'Save changes' : 'Saved'}
        </button>
      </div>
    </section>
  );
}

function BrandVoiceSection() {
  const [websiteUrl, setWebsiteUrl] = useState('https://acmerobotics.com');
  const [isScanning, setIsScanning] = useState(false);
  const [testText, setTestText] = useState(
    'Robots are becoming more common in factories. They help workers do their jobs better and safer. We should not be afraid of them.'
  );
  const [sliders, setSliders] = useState({
    analytical: 70,
    formal: 40,
    humble: 60,
    playful: 30,
  });

  return (
    <div className="space-y-8">
      <section className="grid md:grid-cols-2 gap-8">
        <div className="bg-surface border border-white/5 rounded-2xl p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 bg-blue-500/10 rounded-lg text-blue-400">
              <Globe className="w-5 h-5" />
            </div>
            <div>
              <h2 className="font-bold text-white">Website Source</h2>
              <p className="text-xs text-slate-400">Scan public pages for tone.</p>
            </div>
          </div>
          <div className="flex gap-2 mb-4">
            <input
              type="text"
              value={websiteUrl}
              onChange={(e) => setWebsiteUrl(e.target.value)}
              className="flex-1 bg-black/20 border border-white/10 rounded-lg px-3 py-2 text-sm text-slate-300 focus:outline-none focus:border-primary"
            />
            <button
              onClick={() => setIsScanning(!isScanning)}
              className="px-4 py-2 bg-white/5 hover:bg-white/10 border border-white/10 rounded-lg text-sm font-medium transition-colors"
            >
              {isScanning ? 'Scanning...' : 'Scan'}
            </button>
          </div>
          <div className="text-xs text-slate-500 flex items-center gap-2">
            <CheckCircle2 className="w-3 h-3 text-green-500" />
            <span>Last scanned 48h ago • 24 pages analyzed</span>
          </div>
        </div>

        <section className="bg-surface border border-white/5 rounded-2xl p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 bg-purple-500/10 rounded-lg text-purple-400">
              <FileText className="w-5 h-5" />
            </div>
            <div>
              <h2 className="font-bold text-white">Writing Samples</h2>
              <p className="text-xs text-slate-400">Upload blogs, newsletters, or posts.</p>
            </div>
          </div>
          <div className="border-2 border-dashed border-white/10 rounded-xl p-4 text-center hover:bg-white/5 transition-colors cursor-pointer mb-4">
            <Upload className="w-6 h-6 text-slate-500 mx-auto mb-2" />
            <p className="text-xs text-slate-400">Drop PDF/Doc or paste text</p>
          </div>
          <div className="space-y-2">
            <WritingSampleRow name="Q3_Shareholder_Letter.pdf" />
            <WritingSampleRow name="Manifesto_v2.txt" />
          </div>
        </section>
      </section>

      <section className="bg-surface border border-white/5 rounded-2xl p-8">
        <h2 className="text-xl font-bold text-white mb-6 flex items-center gap-2">
          <Sliders className="w-6 h-6 text-primary" />
          Voice Profile
        </h2>
        <div className="grid md:grid-cols-2 gap-12">
          <div className="space-y-6">
            <VoiceSlider labelLeft="Analytical" labelRight="Conversational" value={sliders.analytical} />
            <VoiceSlider labelLeft="Formal" labelRight="Casual" value={sliders.formal} />
            <VoiceSlider labelLeft="Humble" labelRight="Confident" value={sliders.humble} />
            <VoiceSlider labelLeft="Playful" labelRight="Serious" value={sliders.playful} />
          </div>
          <div className="space-y-6">
            <div>
              <h3 className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">Detected Characteristics</h3>
              <div className="flex flex-wrap gap-2">
                <Badge>Short, punchy sentences</Badge>
                <Badge>Moderate technical jargon</Badge>
                <Badge>First-person plural ("We believe")</Badge>
                <Badge>Optimistic futurism</Badge>
              </div>
            </div>
            <div>
              <h3 className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">Example Output</h3>
              <div className="bg-black/20 border border-white/10 rounded-xl p-4 text-sm text-slate-300 italic leading-relaxed">
                "We aren't just building machines; we're architecting the nervous system of the next industrial revolution. Automation isn't a replacement—it's an amplifier of human intent."
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="grid md:grid-cols-3 gap-8">
        <div className="md:col-span-2 bg-surface border border-white/5 rounded-2xl p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-bold text-white flex items-center gap-2">
              <Zap className="w-5 h-5 text-yellow-400" />
              Test Playground
            </h2>
            <div className="flex gap-2">
              <button className="text-xs text-slate-400 hover:text-white">Reset</button>
            </div>
          </div>
          <div className="relative">
            <textarea
              value={testText}
              onChange={(e) => setTestText(e.target.value)}
              className="w-full h-40 bg-black/20 border border-white/10 rounded-xl p-4 text-slate-300 focus:outline-none focus:border-primary resize-none"
            />
            <div className="absolute bottom-4 right-4 flex gap-2">
              <button className="px-3 py-1.5 bg-surface border border-white/10 rounded-lg text-xs font-medium text-slate-300 hover:text-white transition-colors">
                More Formal
              </button>
              <button className="px-3 py-1.5 bg-primary text-white rounded-lg text-xs font-bold hover:bg-primary-hover transition-colors flex items-center gap-1">
                <Sparkles className="w-3 h-3" /> Apply Brand Voice
              </button>
            </div>
          </div>
        </div>
        <div className="bg-surface border border-white/5 rounded-2xl p-6">
          <h2 className="font-bold text-white mb-4">Settings</h2>
          <div className="space-y-4">
            <Toggle label="Apply to Public Insights" checked={true} />
            <Toggle label="Apply to Blog Generation" checked={true} />
            <Toggle label="Show 'In Voice' Badge" checked={false} />
            <div className="pt-4 border-t border-white/10">
              <div className="flex items-start gap-2 p-3 bg-yellow-500/5 rounded-lg border border-yellow-500/10">
                <AlertTriangle className="w-4 h-4 text-yellow-500 mt-0.5" />
                <p className="text-xs text-yellow-200/80 leading-relaxed">
                  PodVault will never alter direct quotes from guests, regardless of these settings.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

function WritingSampleRow({ name }) {
  return (
    <div className="flex items-center justify-between text-sm p-2 bg-white/5 rounded-lg">
      <div className="flex items-center gap-2">
        <FileText className="w-4 h-4 text-slate-500" />
        <span className="text-slate-300 truncate w-32">{name}</span>
      </div>
      <button className="text-slate-500 hover:text-red-400">
        <Trash2 className="w-4 h-4" />
      </button>
    </div>
  );
}

function VoiceSlider({ labelLeft, labelRight, value }) {
  return (
    <div>
      <div className="flex justify-between text-xs text-slate-400 mb-2">
        <span>{labelLeft}</span>
        <span>{labelRight}</span>
      </div>
      <div className="h-2 bg-white/10 rounded-full relative">
        <div
          className="absolute top-1/2 -translate-y-1/2 w-4 h-4 bg-primary rounded-full shadow-lg cursor-pointer hover:scale-110 transition-transform"
          style={{ left: `${value}%` }}
        />
        <div className="absolute top-0 left-0 h-full bg-primary/30 rounded-full" style={{ width: `${value}%` }} />
      </div>
    </div>
  );
}

function Badge({ children }) {
  return (
    <span className="px-2 py-1 rounded bg-white/5 border border-white/10 text-xs text-slate-300">{children}</span>
  );
}

function Toggle({ label, checked }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-sm text-slate-300">{label}</span>
      <div className={`w-10 h-5 rounded-full relative transition-colors ${checked ? 'bg-primary' : 'bg-white/10'}`}>
        <div
          className={`absolute top-1 bottom-1 w-3 h-3 bg-white rounded-full transition-all ${
            checked ? 'left-6' : 'left-1'
          }`}
        />
      </div>
    </div>
  );
}

function BrandTeamSection({ project }) {
  const [members, setMembers] = useState([]);
  const [invites, setInvites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('viewer');
  const [isInviteModalOpen, setIsInviteModalOpen] = useState(false);
  const [inviteLoading, setInviteLoading] = useState(false);
  const [lastInviteLink, setLastInviteLink] = useState(null);

  async function loadTeam() {
    try {
      setLoading(true);
      const res = await getProjectMembers(project.id);
      if (res?.data) {
        setMembers(res.data.members || []);
        setInvites(res.data.invites || []);
      }
    } catch (e) {
      console.error('Failed to load team', e);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadTeam();
  }, [project.id]);

  const handleInvite = async (e) => {
    e.preventDefault();
    setInviteLoading(true);
    setLastInviteLink(null);
    try {
      const res = await inviteProjectMember(project.id, inviteEmail, inviteRole);
      if (res?.success) {
        setInviteEmail('');
        setLastInviteLink(res.inviteLink || null);
        loadTeam(); // Refresh list
        if (res.addedDirectly) {
            // If added directly, we can close modal, or show success toast
            setIsInviteModalOpen(false);
        }
      } else {
        alert(res?.error || 'Failed to invite');
      }
    } catch (e) {
      console.error(e);
      alert('Failed to invite user');
    } finally {
      setInviteLoading(false);
    }
  };

  const handleRemoveMember = async (userId) => {
    if (!window.confirm('Are you sure you want to remove this member?')) return;
    try {
      await removeProjectMember(project.id, userId);
      loadTeam();
    } catch (e) {
      console.error(e);
      alert('Failed to remove member');
    }
  };

  const handleRevokeInvite = async (inviteId) => {
    if (!window.confirm('Are you sure you want to revoke this invite?')) return;
    try {
      await revokeProjectInvite(project.id, inviteId);
      loadTeam();
    } catch (e) {
      console.error(e);
      alert('Failed to revoke invite');
    }
  };

  return (
    <div className="space-y-8">
        <section className="bg-surface border border-white/5 rounded-2xl p-6">
            <div className="flex items-center justify-between mb-6">
                <div>
                    <h2 className="text-lg font-bold text-white">Team Members</h2>
                    <p className="text-xs text-slate-400">Manage who has access to this brand.</p>
                </div>
                <button 
                    onClick={() => setIsInviteModalOpen(true)}
                    className="px-4 py-2 bg-primary text-white rounded-lg text-xs font-bold hover:bg-primary-hover transition-colors flex items-center gap-2"
                >
                    <Plus className="w-4 h-4" /> Invite Member
                </button>
            </div>

            {loading ? (
                <div className="text-sm text-slate-400">Loading team...</div>
            ) : (
                <div className="space-y-6">
                    {/* Active Members */}
                    <div className="space-y-3">
                        <h3 className="text-xs font-bold text-slate-500 uppercase tracking-wider">Active Members ({members.length})</h3>
                        {members.map(member => (
                            <div key={member.id} className="flex items-center justify-between p-3 bg-white/5 rounded-xl border border-white/5">
                                <div className="flex items-center gap-3">
                                    <div className="w-8 h-8 rounded-full bg-blue-500/20 text-blue-400 flex items-center justify-center font-bold text-xs border border-blue-500/30">
                                        {member.user?.first_name?.[0] || member.user?.email?.[0]?.toUpperCase() || 'U'}
                                    </div>
                                    <div>
                                        <div className="text-sm font-medium text-white">
                                            {member.user?.full_name || member.user?.email} 
                                            {member.user_id === project.created_by_user_id && <span className="ml-2 text-[10px] bg-primary/20 text-primary px-1.5 py-0.5 rounded border border-primary/30">Owner</span>}
                                        </div>
                                        <div className="text-xs text-slate-500">{member.user?.email}</div>
                                    </div>
                                </div>
                                <div className="flex items-center gap-4">
                                    <Badge>{member.role}</Badge>
                                    {member.role !== 'owner' && (
                                        <button onClick={() => handleRemoveMember(member.user_id)} className="text-slate-500 hover:text-red-400 transition-colors">
                                            <Trash2 className="w-4 h-4" />
                                        </button>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* Pending Invites */}
                    {invites.length > 0 && (
                        <div className="space-y-3 pt-4 border-t border-white/10">
                            <h3 className="text-xs font-bold text-slate-500 uppercase tracking-wider">Pending Invites ({invites.length})</h3>
                            {invites.map(invite => (
                                <div key={invite.id} className="flex items-center justify-between p-3 bg-white/5 rounded-xl border border-white/5 border-dashed">
                                    <div className="flex items-center gap-3">
                                        <div className="w-8 h-8 rounded-full bg-slate-500/20 text-slate-400 flex items-center justify-center font-bold text-xs border border-slate-500/30">
                                            <Mail className="w-4 h-4" />
                                        </div>
                                        <div>
                                            <div className="text-sm font-medium text-slate-300">{invite.email}</div>
                                            <div className="text-xs text-slate-500">Invited as {invite.role}</div>
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-3">
                                        <button 
                                            onClick={() => navigator.clipboard.writeText(`${window.location.origin}/join?token=${invite.token}`)}
                                            className="text-xs text-primary hover:text-white flex items-center gap-1"
                                            title="Copy Invite Link"
                                        >
                                            <Copy className="w-3 h-3" /> Copy Link
                                        </button>
                                        <button onClick={() => handleRevokeInvite(invite.id)} className="text-slate-500 hover:text-red-400 transition-colors">
                                            <Trash2 className="w-4 h-4" />
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}
        </section>

        {/* Invite Modal */}
        {isInviteModalOpen && (
            <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
                <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={() => setIsInviteModalOpen(false)} />
                <div className="relative bg-surface border border-white/10 rounded-2xl p-6 max-w-md w-full shadow-2xl animate-in fade-in zoom-in duration-200">
                    <div className="flex items-center justify-between mb-6">
                        <h3 className="text-xl font-bold text-white">Invite Member</h3>
                        <button onClick={() => setIsInviteModalOpen(false)} className="text-slate-500 hover:text-white">
                            <X className="w-5 h-5" />
                        </button>
                    </div>
                    
                    {!lastInviteLink ? (
                        <form onSubmit={handleInvite} className="space-y-4">
                            <div>
                                <label className="block text-xs font-bold text-slate-400 uppercase mb-1">Email Address</label>
                                <input 
                                    type="email" 
                                    required 
                                    value={inviteEmail}
                                    onChange={(e) => setInviteEmail(e.target.value)}
                                    className="w-full bg-black/20 border border-white/10 rounded-lg p-3 text-white focus:ring-1 focus:ring-primary outline-none transition-all"
                                    placeholder="colleague@company.com"
                                />
                            </div>
                            <div>
                                <label className="block text-xs font-bold text-slate-400 uppercase mb-1">Role</label>
                                <select 
                                    value={inviteRole}
                                    onChange={(e) => setInviteRole(e.target.value)}
                                    className="w-full bg-black/20 border border-white/10 rounded-lg p-3 text-white focus:ring-1 focus:ring-primary outline-none transition-all"
                                >
                                    <option value="viewer">Viewer (Read Only)</option>
                                    <option value="editor">Editor (Can Edit Content)</option>
                                    <option value="admin">Admin (Manage Settings)</option>
                                </select>
                            </div>
                            
                            <button type="submit" disabled={inviteLoading} className="w-full py-3 bg-primary hover:bg-primary-hover text-white font-bold rounded-lg transition-colors flex items-center justify-center gap-2">
                                {inviteLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Send Invite'}
                            </button>
                        </form>
                    ) : (
                        <div className="space-y-4 text-center">
                            <div className="w-16 h-16 bg-green-500/20 text-green-500 rounded-full flex items-center justify-center mx-auto mb-4">
                                <CheckCircle2 className="w-8 h-8" />
                            </div>
                            <h4 className="text-lg font-bold text-white">Invite Created!</h4>
                            <p className="text-sm text-slate-400">Share this link with your teammate:</p>
                            
                            <div className="flex items-center gap-2 bg-black/30 p-2 rounded-lg border border-white/10 mt-2">
                                <code className="flex-1 text-xs text-slate-300 truncate">{window.location.origin}{lastInviteLink}</code>
                                <button 
                                    onClick={() => navigator.clipboard.writeText(`${window.location.origin}${lastInviteLink}`)}
                                    className="p-2 hover:bg-white/10 rounded text-slate-400 hover:text-white"
                                >
                                    <Copy className="w-4 h-4" />
                                </button>
                            </div>

                            <button 
                                onClick={() => {
                                    setLastInviteLink(null);
                                    setIsInviteModalOpen(false);
                                }}
                                className="w-full py-3 mt-4 bg-white/10 hover:bg-white/20 text-white font-bold rounded-lg transition-colors"
                            >
                                Done
                            </button>
                        </div>
                    )}
                </div>
            </div>
        )}
    </div>
  );
}
